import React, { useState } from 'react';
import { Order } from '@hero/schema';
import { useTranslation } from 'react-i18next';
import { formatTemperatureText } from '@hero/core';

import {
  WeightIcon,
  LoadMainIcon,
  TempIcon,
  QuantityIcon,
  ValueIcon,
  PencilIcon,
} from '@hero/web-icons';
import { OrderWeightDisplay } from '../../Item/OrderWeightDisplay';
import { Collapse } from '@hero/web-components';
import { Price } from '../../../../app/Price';
import { DetailRow } from './DetailRow';
import { OrderLoadEditDrawer } from '../OrderLoadEditDrawer';

export interface LoadDetailsProps {
  order: Order;
}

export const LoadDetails: React.FC<LoadDetailsProps> = ({ order }) => {
  const { t } = useTranslation('m-order');
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Phase 2 (relations-v2): orders may publish without an explicit
  // item — `load.items[]` is now optional and defaults to empty. The
  // detail panel hides per-item rows in that case so we don't render
  // a confusing "—" everywhere; the totals (weight, temperature) and
  // the rest of the order detail still show. Once the carrier fills
  // in load detail later (via Monday or the order-update API), this
  // section automatically picks the new values up.
  const item = order.load.items[0];

  // Phase 7 (relations-v2): `Order.payment` is gone; currency for any
  // money rendering on the load panel comes from the first contract
  // leg's amount block. When no contract has been struck yet (auto /
  // own_fleet pre-allocation) we fall back to the platform default
  // (`SAR`), which is what the cargo-value row used to read off
  // `order.payment.beneficiary.amount.currency` for.
  const goodsValueCurrency = order.contracts?.[0]?.amount.currency ?? 'SAR';

  return (
    <div className="p-5 bg-white border border-gray-200 rounded-lg shadow-sm">
      <Collapse
        title={t('load_details.title', 'Load Details')}
        icon={
          <div className="p-1.5 bg-orange-50 rounded-lg">
            <LoadMainIcon className="size-5 text-orange-600" />
          </div>
        }
        action={
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setIsEditOpen(true);
            }}
            className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
            aria-label={t('drawer.load_edit.title', { defaultValue: 'Edit load details' })}>
            <PencilIcon className="w-3.5 h-3.5 text-gray-600" />
          </button>
        }
        defaultOpen={true}>
        <div className="space-y-0">
          {/* Product - Combined Shipment Type & Description */}
          {item && (
            <div className="py-3 border-b border-gray-200">
              <div className="flex flex-row items-center justify-between">
                <div className="flex flex-row items-center justify-start gap-2">
                  <LoadMainIcon className="size-5 text-gray-900" />
                  <p className="text-gray-600 text-[14px] font-[400] leading-[22px]">
                    {t('load_details.product')}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  {item.name && (
                    <div className="px-3 py-1.5 bg-gray-50 rounded-lg">
                      <p className="text-gray-900 text-[14px] font-[500] leading-[22px]">
                        {item.name}
                      </p>
                    </div>
                  )}
                  {item.type && (
                    <p className="text-gray-500 text-[12px] leading-[18px]">
                      {t(`item_types.${item.type}`, item.type)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          <DetailRow
            icon={<WeightIcon className="size-5 text-gray-900" />}
            label={t('load_details.weights')}
            value={
              <OrderWeightDisplay
                weight={order.load.totalItemsWeight}
                convertWeightTo="ton"
                className="text-gray-900 text-[14px] font-[500] leading-[22px]"
              />
            }
          />

          {order.load.temperature?.types?.[0] && (
            <DetailRow
              icon={<TempIcon className="size-5 text-gray-900" />}
              label={t('load_details.temperature')}
              value={
                formatTemperatureText(
                  order.load.temperature,
                  t(
                    `temperature_types.${order.load.temperature.types[0]}`,
                    order.load.temperature.types[0],
                  ),
                ) ||
                t(
                  `temperature_types.${order.load.temperature.types[0]}`,
                  order.load.temperature.types[0],
                )
              }
            />
          )}

          {item && item.quantity != null && item.packing && (
            <DetailRow
              icon={<QuantityIcon className="size-5 text-gray-900" />}
              label={t('load_details.quantity')}
              value={`${item.quantity} ${t(
                `packing_types.${item.packing}`,
                item.packing,
              )}`}
            />
          )}

          {item && (
            <DetailRow
              icon={<ValueIcon className="size-5 text-gray-900" />}
              label={t('load_details.goods_value')}
              value={
                <Price
                  amount={item.cost || 0}
                  currency={goodsValueCurrency}
                />
              }
              showDivider={false}
            />
          )}
        </div>
      </Collapse>

      <OrderLoadEditDrawer
        order={order}
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
      />
    </div>
  );
};
